import { type BaseRoleGroup, type RoleGroup, RoleType } from '../../models/Role'
import { type InviteFilter } from '../../store/auth/auth'
import { type GetUsersRequest } from '../../store/user/user'
import { roleGroups } from './InviteRoleGroups'
import { defaultPendingInvitationsFilter } from './PendingInvitations/utils'
import { type InviteChipKey, type MemberChipKey } from './teamChips'

export interface TeamQueryFilters {
  namePattern?: string | null
  roleGroupNames?: string[]
}

interface ListPage {
  page: number
  perPage: number
}

const toBaseRoleGroups = (groups: RoleGroup[]): BaseRoleGroup[] => groups.map(({ name, roleIds }) => ({ name, roleIds }))

const selectedGroups = (roleGroupNames: string[] = []) => roleGroups.filter((group) => roleGroupNames.includes(group.name))

export const seatTypeRoleGroups = (seatType: RoleType): BaseRoleGroup[] => toBaseRoleGroups(roleGroups.filter((group) => group.type === seatType))

export const memberListArgs = (accountId: string, chip: MemberChipKey, { page, perPage }: ListPage, filters: TeamQueryFilters = {}): GetUsersRequest => {
  const roleGroups = selectedGroups(filters.roleGroupNames).map((group) => group.roleIds)
  return {
    accountId,
    pageRequest: { page: page - 1, size: perPage },
    identityTypes: ['idp'],
    name: filters.namePattern ?? undefined,
    statuses: [chip],
    roleGroups: roleGroups.length > 0 ? roleGroups : undefined,
  }
}

export const memberCountArgs = (accountId: string, chip: MemberChipKey): GetUsersRequest => ({
  accountId,
  pageRequest: { page: 0, size: 1 },
  identityTypes: ['idp'],
  statuses: [chip],
})

export const inviteFilter = (chip: InviteChipKey, filters: TeamQueryFilters = {}): InviteFilter => {
  const base: InviteFilter = {
    ...defaultPendingInvitationsFilter,
    namePattern: filters.namePattern ?? null,
    roleGroups: toBaseRoleGroups(selectedGroups(filters.roleGroupNames)),
  }
  return chip === 'requested' ? { ...base, status: null, types: ['invite_request'] } : { ...base, status: chip }
}

export const inviteCountFilter = (chip: InviteChipKey): InviteFilter => inviteFilter(chip)

export const requestedGroupFilter = (seatType: RoleType, filters: TeamQueryFilters = {}): InviteFilter | null => {
  const seatGroups = seatTypeRoleGroups(seatType)
  const selected = filters.roleGroupNames ?? []
  const roleGroups = selected.length > 0 ? seatGroups.filter((group) => selected.includes(group.name)) : seatGroups

  if (roleGroups.length === 0) {
    return null
  }

  return {
    ...defaultPendingInvitationsFilter,
    namePattern: filters.namePattern ?? null,
    types: ['invite_request'],
    roleGroups,
  }
}

export const requestedSeatTypeCountFilter = (seatType: RoleType): InviteFilter => ({
  ...defaultPendingInvitationsFilter,
  types: ['invite_request'],
  roleGroups: seatTypeRoleGroups(seatType),
})
